import { createContext, memo, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { reportErrorToBugsnag } from '@air/utils-error';

import usePrevious from '~/hooks/usePrevious';
import { centralizedClipIdSelector } from '~/store/centralizedClip/selectors';
import { isDevOrTestStage } from '~/swr-hooks/utils';

export const ASSET_MODAL_LEFT_PANEL_OPEN_TABS = ['transcript', 'chapters', 'people', 'captions'] as const;

export type AssetModalLeftPanelOpenTab = (typeof ASSET_MODAL_LEFT_PANEL_OPEN_TABS)[number];

export type AssetModalLeftPanelsTab = AssetModalLeftPanelOpenTab | 'none';

export const isAssetModalLeftPanelTabOpen = (tab: AssetModalLeftPanelsTab): tab is AssetModalLeftPanelOpenTab =>
  tab !== 'none';

export const leftContentPanels = ['pdfOutline'] as const;

export type LeftContentPanelOpenPanel = (typeof leftContentPanels)[number];

export type LeftContentPanel = LeftContentPanelOpenPanel | 'none';

export const isAssetModalLeftContentPanelOpen = (panel: LeftContentPanel): panel is LeftContentPanelOpenPanel =>
  panel !== 'none';

export type AssetModalLeftPanelContextValue = {
  activeLeftPanelTab: AssetModalLeftPanelsTab;
  isLeftPanelOpen: boolean;
  isLeftPanelTabOpen: (tab: AssetModalLeftPanelOpenTab) => boolean;
  showLeftPanelTab: (tab: AssetModalLeftPanelOpenTab) => void;
  closeLeftPanel: () => void;
  activeLeftContentPanel: LeftContentPanel;
  isLeftContentPanelOpen: boolean;
  isLeftContentPanelTabOpen: (panel: LeftContentPanelOpenPanel) => boolean;
  showLeftContentPanel: (panel: LeftContentPanelOpenPanel) => void;
  closeLeftContentPanel: () => void;
};

const defaultContextValue: AssetModalLeftPanelContextValue = {
  activeLeftPanelTab: 'none',
  isLeftPanelOpen: false,
  isLeftPanelTabOpen: () => false,
  showLeftPanelTab: () => {},
  closeLeftPanel: () => {},
  activeLeftContentPanel: 'none',
  isLeftContentPanelOpen: false,
  isLeftContentPanelTabOpen: () => false,
  showLeftContentPanel: () => {},
  closeLeftContentPanel: () => {},
};

export const AssetModalLeftPanelContext = createContext<AssetModalLeftPanelContextValue>(defaultContextValue);

export const useAssetModalLeftPanel = () => {
  const ctx = useContext(AssetModalLeftPanelContext);

  if (ctx === defaultContextValue) {
    const error = new Error('useAssetModalLeftPanel must be used within AssetModalLeftPanelContextProvider');

    if (isDevOrTestStage()) {
      throw error;
    }

    reportErrorToBugsnag({
      error,
      context: 'Asset Modal left panel context not found',
    });
  }

  return ctx;
};

export type AssetModalLeftPanelContextProviderProps = { children: ReactNode };

export const AssetModalLeftPanelContextProvider = memo(({ children }: AssetModalLeftPanelContextProviderProps) => {
  const [activeLeftPanelTab, setActiveLeftPanelTabState] = useState<AssetModalLeftPanelsTab>('none');
  const [activeLeftContentPanel, setActiveLeftContentPanel] = useState<LeftContentPanel>('none');

  const clipId = useSelector(centralizedClipIdSelector);
  const previousClipId = usePrevious(clipId);

  // Reset panels when navigating to a different asset
  useEffect(() => {
    if (previousClipId !== undefined && previousClipId !== clipId) {
      setActiveLeftContentPanel('none');
    }
  }, [clipId, previousClipId]);

  const isLeftPanelTabOpen = useCallback(
    (tab: AssetModalLeftPanelOpenTab) => activeLeftPanelTab === tab,
    [activeLeftPanelTab],
  );

  const showLeftPanelTab = useCallback((tab: AssetModalLeftPanelOpenTab) => {
    setActiveLeftContentPanel('none');
    setActiveLeftPanelTabState(tab);
  }, []);

  const closeLeftPanel = useCallback(() => {
    setActiveLeftPanelTabState('none');
  }, []);

  const isLeftContentPanelTabOpen = useCallback(
    (panel: LeftContentPanelOpenPanel) => activeLeftContentPanel === panel,
    [activeLeftContentPanel],
  );

  const showLeftContentPanel = useCallback((panel: LeftContentPanelOpenPanel) => {
    setActiveLeftContentPanel(panel);
  }, []);

  const closeLeftContentPanel = useCallback(() => {
    setActiveLeftContentPanel('none');
  }, []);

  const contextValue = useMemo(
    () => ({
      activeLeftPanelTab,
      isLeftPanelOpen: isAssetModalLeftPanelTabOpen(activeLeftPanelTab),
      isLeftPanelTabOpen,
      showLeftPanelTab,
      closeLeftPanel,
      activeLeftContentPanel,
      isLeftContentPanelOpen: isAssetModalLeftContentPanelOpen(activeLeftContentPanel),
      isLeftContentPanelTabOpen,
      showLeftContentPanel,
      closeLeftContentPanel,
    }),
    [
      activeLeftPanelTab,
      isLeftPanelTabOpen,
      showLeftPanelTab,
      closeLeftPanel,
      activeLeftContentPanel,
      isLeftContentPanelTabOpen,
      showLeftContentPanel,
      closeLeftContentPanel,
    ],
  );

  return <AssetModalLeftPanelContext.Provider value={contextValue}>{children}</AssetModalLeftPanelContext.Provider>;
});

AssetModalLeftPanelContextProvider.displayName = 'AssetModalLeftPanelContextProvider';
